import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Button, Input, Label, Table, Row, Col, Spinner } from 'reactstrap';
import gridApi from '../../api/gridApi';
import { showConfirm, showSuccess, showError } from '../../../utils/sweetAlertHelper';

function GlobalBotControl() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reason, setReason] = useState('');
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStatus();
    fetchHistory();
    const interval = setInterval(() => {
      fetchStatus();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await gridApi.getGlobalStatus();
      setStatus(response);
      setError(null);
    } catch (error) {
      console.error('Error fetching status:', error);
      setError(error.response?.data?.error || error.message);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await gridApi.getGlobalHistory({ limit: 10 });
      setHistory(response.history || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleStopAll = async () => {
    const result = await showConfirm(
      'Las órdenes NO se cancelarán, solo se detendrá el motor del bot.',
      '⚠️ ¿Detener todos los bots del sistema?',
      { confirmButtonText: 'Sí, detener todos' }
    );
    
    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await gridApi.stopAllBots({ reason: reason || 'Admin stop' });
      await showSuccess(
        `Detenidos: ${response.stopped}\nFallidos: ${response.failed}`,
        '✅ Bots Detenidos'
      );
      setReason('');
      await fetchStatus();
      await fetchHistory();
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      setError(errorMsg);
      await showError(errorMsg, 'Error al Detener Bots');
    } finally {
      setLoading(false);
    }
  };

  const handleStartAll = async () => {
    const result = await showConfirm(
      'Se reactivarán automáticamente todos los bots que estaban activos.',
      '¿Iniciar todos los bots del sistema?',
      { confirmButtonText: 'Sí, iniciar todos' }
    );
    
    if (!result.isConfirmed) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await gridApi.startAllBots({ reason: reason || 'Admin start' });
      await showSuccess(
        `Iniciados: ${response.started}\nFallidos: ${response.failed}`,
        '✅ Bots Iniciados'
      );
      setReason('');
      await fetchStatus();
      await fetchHistory();
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      setError(errorMsg);
      await showError(errorMsg, 'Error al Iniciar Bots');
    } finally {
      setLoading(false);
    }
  };

  if (!status) {
    return (
      <Card>
        <CardBody className="text-center">
          <Spinner color="primary" /> Cargando estado global...
        </CardBody>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="bg-dark text-white">
        <h5 className="mb-0">🎛️ Control Global del Sistema</h5>
      </CardHeader>
      <CardBody>
        {/* Estado Actual */}
        <Row className="mb-3">
          <Col md={6}>
            <div className={`alert ${status.systemEnabled ? 'alert-success' : 'alert-danger'} mb-0`}>
              <h6 className="mb-1">
                <strong>Estado del Sistema:</strong> {status.systemEnabled ? '✅ ACTIVO' : '⏹️ DETENIDO'}
              </h6>
              <p className="mb-0"><strong>Bots activos:</strong> {status.activeBots || 0}</p>
              {status.lastChangeAt && (
                <small>Último cambio: {new Date(status.lastChangeAt).toLocaleString()}</small>
              )}
            </div>
          </Col>
          <Col md={6}>
            {error && (
              <div className="alert alert-warning">
                <strong>⚠️ Advertencia:</strong> {error}
              </div>
            )}
          </Col>
        </Row>

        {/* Formulario de Control */}
        <Row className="mb-3">
          <Col md={12}>
            <Label>Razón del cambio (opcional):</Label>
            <Input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: Mantenimiento programado"
              maxLength={200}
              bsSize="sm"
            />
          </Col>
        </Row>

        <Row className="mb-3">
          <Col md={6} className="d-grid">
            <Button
              onClick={handleStopAll}
              disabled={loading || !status.systemEnabled}
              color="danger"
              size="sm"
            >
              {loading ? <><Spinner size="sm" /> Deteniendo...</> : '⏹️ DETENER TODOS'}
            </Button>
          </Col>
          <Col md={6} className="d-grid">
            <Button
              onClick={handleStartAll}
              disabled={loading || status.systemEnabled}
              color="success"
              size="sm"
            >
              {loading ? <><Spinner size="sm" /> Iniciando...</> : '▶️ INICIAR TODOS'}
            </Button>
          </Col>
        </Row>

        <div className="alert alert-info">
          <p className="mb-0"><strong>ℹ️ Información:</strong></p>
          <ul className="mb-0 mt-2">
            <li><strong>DETENER:</strong> Para todos los bots sin cancelar órdenes en Binance</li>
            <li><strong>INICIAR:</strong> Reactiva automáticamente los bots que estaban activos</li>
          </ul>
        </div>

        {/* Historial */}
        {history.length > 0 && (
          <>
            <hr />
            <h6>Historial de Cambios Recientes</h6>
            <Table size="sm" striped>
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Acción</th>
                  <th>Usuario</th>
                  <th>Razón</th>
                  <th>Bots</th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry) => (
                  <tr key={entry._id}>
                    <td><small>{new Date(entry.timestamp).toLocaleString()}</small></td>
                    <td>
                      <span className={`badge ${entry.action === 'START_ALL' ? 'bg-success' : 'bg-danger'}`}>
                        {entry.action === 'START_ALL' ? '▶️ START' : '⏹️ STOP'}
                      </span>
                    </td>
                    <td><small>{entry.performedBy?.nombre || 'N/A'}</small></td>
                    <td><small>{entry.reason || '-'}</small></td>
                    <td><small>{entry.affectedBots || 0}</small></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </>
        )}
      </CardBody>
    </Card>
  );
}

export default GlobalBotControl;